/**
 * Euthanasia consent form — PDF component and field definitions.
 *
 * Auto-filled: clinic header, owner details, patient details, vet name.
 * Manual fields: body disposition choice (radio).
 */
import { Document, Page, StyleSheet, Text, View } from '@react-pdf/renderer'

import { ClinicHeader } from '@/frontend/features/printing/documents/blocks/clinic-header'
import { OwnerDetails } from '@/frontend/features/printing/documents/blocks/owner-details'
import { OwnerSignature } from '@/frontend/features/printing/documents/blocks/owner-signature'
import { PatientDetails } from '@/frontend/features/printing/documents/blocks/patient-details'
import { RadioGroupField } from '@/frontend/features/printing/documents/blocks/pdf-fields'
import { baseStyles } from '@/frontend/features/printing/documents/blocks/pdf-styles'
import { VetSignoff } from '@/frontend/features/printing/documents/blocks/vet-signoff'
import type { DocumentContext } from '@/frontend/features/printing/documents/types'

export interface EuthanasiaConsentFields {
  bodyDisposition: string
  vetName: string
}

export const defaultEuthanasiaConsentFields: EuthanasiaConsentFields = {
  bodyDisposition: '',
  vetName: '',
}

const TITLE = 'EUTHANASIA CONSENT FORM'

export const DISPOSITION_OPTIONS = [
  { value: 'home_burial', label: 'Taken home for burial' },
  { value: 'communal_cremation', label: 'Communal cremation (£37)' },
  {
    value: 'individual_scatter',
    label:
      'Individual cremation with ashes returned in scatter tube (£196 for cats & £260.50 for dogs)',
  },
  {
    value: 'individual_casket',
    label:
      'Individual cremation with ashes returned in wooden casket (£196 for cats & £260.50 for dogs)',
  },
] as const

function consentText(patientName: string) {
  return `I hereby acknowledge that I am the owner of ${patientName}, and request the veterinary surgeon of the clinic to euthanise this pet.`
}

function paymentNotice(patientName: string) {
  return `Please note that payment for cremation must be made before ${patientName} can be sent to the crematorium.`
}

const FALLBACK_NOTICE =
  'If you are unable to decide on cremation arrangements at this time, please let us know within 10 days of euthanasia. If we do not hear from you within this time, we will proceed with communal cremation, and ashes will not be returned.'

const styles = StyleSheet.create({
  ...baseStyles,
})

export function EuthanasiaConsentPdf({
  context,
  fields,
}: {
  context: DocumentContext
  fields: EuthanasiaConsentFields
}) {
  const { patient, owner, clinic } = context

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <ClinicHeader clinic={clinic} />

        <Text style={styles.title}>{TITLE}</Text>

        <OwnerDetails owner={owner} />
        <View style={styles.sectionGap}>
          <PatientDetails
            patient={patient}
            weight={context.weight}
            showDob
            showWeight
            showMicrochip
          />
        </View>

        {/* Consent paragraph */}
        <View style={styles.sectionGap}>
          <Text style={styles.text}>{consentText(patient.name)}</Text>
        </View>

        {/* Body disposition */}
        <View style={styles.sectionGap}>
          <Text style={styles.text}>I request for the body to be:</Text>
          <RadioGroupField value={fields.bodyDisposition} options={DISPOSITION_OPTIONS} />
        </View>

        {/* Notices */}
        <View style={styles.sectionGap}>
          <Text style={{ ...styles.text, ...styles.bold }}>{paymentNotice(patient.name)}</Text>
        </View>
        <View style={styles.sectionGap}>
          <Text style={styles.text}>{FALLBACK_NOTICE}</Text>
        </View>

        <OwnerSignature owner={owner} />
        <VetSignoff vetName={fields.vetName} />
      </Page>
    </Document>
  )
}
